/*
 * Context
 *
 * Turns what the flow already knows into session variables GRACI can read.
 *
 * THIS IS THE POINT OF PUTTING THE CHAT AT THE END OF A DECISION TREE. By the
 * time somebody reaches it they have answered six questions, and the difference
 * between a useful assistant and an irritating one is whether it makes them
 * type all six again.
 *
 * IT GOES IN `session`, NOT `instructions`. That distinction is the server's,
 * and it is easy to get backwards. `instructions` is a closed vocabulary of
 * four typed objects the server knows how to expand: comm_link_offline,
 * sensor_clone_process, sensor_details and troubleshooting. Anything else
 * handed to it falls through the switch and is silently dropped. `session` is
 * open: the server turns every truthy key into a numbered line and injects it
 * as a hidden priming message, which is exactly the shape a tree's answers
 * want. Falsy values are dropped server side, so nothing needs nulling here.
 *
 * Written against plain question and answer pairs rather than against the
 * Support Center's flow state, so the replicated website can hand it whatever
 * it happens to know without either app learning the other's shapes.
 */

const ANSWERS = 'what_the_customer_already_told_us';

// True when the session carries answers from the guided flow.
export const hasAnswers = variables => Boolean(variables?.[ANSWERS]);

// The server renders each entry as "n. <key> is <value>", so keys read as
// sentence subjects and values as plain prose.
export const session = ({
  answers = [],
  brand,
  documents = [],
  extra = {},
  facts = {},
  reason
} = {}) => {
  let variables = {
    ...extra,
    platform: 'web'
  };
  if (brand) {
    variables.the_customers_system = brand;
  }

  /*
   * The answers, in order, as one line. Kept as prose rather than JSON: the
   * reader is a language model, and a sentence about what was already asked
   * survives a change to the tree better than a schema the server would have
   * to be taught.
   */
  let transcript = answers.filter(entry => entry?.question && entry?.answer).map(entry => `asked "${entry.question}" they answered "${entry.answer}"`).join('; ');
  if (transcript) {
    variables[ANSWERS] = `they have already been through our guided troubleshooting, where we ${transcript}. do not ask them to repeat any of it`;
  }
  if (reason) {
    variables.why_they_still_need_help = reason;
  }
  Object.entries(facts).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== false && value !== '') {
      variables[key] = value;
    }
  });

  /*
   * The articles they were shown and rejected. Without this the assistant
   * opens by suggesting the cleaning article they just told us did not work,
   * which is the fastest way to lose somebody's confidence.
   */
  if (documents.length > 0) {
    variables.articles_they_already_read_and_rejected = `${documents.join(', ')}. do not suggest these again`;
  }
  return variables;
};
const Context = {
  hasAnswers,
  session
};
export default Context;